import React, { useEffect, useState, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { isInRnWebView } from '../bridge/webviewBridge';

const TOAST_EVENT = 'plhouse:inapp-toast';
const TOAST_DURATION_MS = 6000;
const MAX_STACK = 4;

/**
 * 외부에서 인앱 토스트를 띄우는 함수.
 * payload: { title, body, type, data }
 *   - type: 'chat' | 'notice' | 'meeting_new' | 'meeting_done' | 'meeting_soon'
 *           | 'article' | 'route' | 'route_new' | 'move_report' | 'schedule_new' | 'quest_done'
 *           | 'youtube_toss_assign' | 'youtube_toss_result' | etc
 */
export const dispatchInAppToast = (payload) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(TOAST_EVENT, { detail: payload }));
};

// 앱과 동일한 "풀잎~" 알림음 (public/pulip.wav). 브라우저 자동재생 정책상
// 최초 사용자 제스처 이후부터 소리가 남.
// 사용자가 고른 알림음(localStorage 'plhouse.notifSound')으로 재생. 기본 풀잎.
const SOUND_FILES = { pulip: '/pulip.wav', sound2: '/synth_brass.mp3' };
let _notiAudio = null;
let _notiAudioKey = null;
const playNotiSound = () => {
  if (typeof window === 'undefined') return;
  // 앱(RN WebView)에서는 네이티브 알림(notifee)이 '알림 볼륨' 스트림으로 소리를 낸다.
  // 웹 HTML오디오는 '미디어 볼륨'으로 나가서, 앱에서 같이 재생하면 이중 소리 + 미디어볼륨으로만
  // 조절되는 문제가 생김 → 앱에서는 웹 사운드를 끄고 notifee에만 맡긴다. (PC 웹은 그대로 재생)
  if (isInRnWebView()) return;
  try {
    let key = 'pulip';
    try { key = localStorage.getItem('plhouse.notifSound') || 'pulip'; } catch {}
    const src = SOUND_FILES[key] || SOUND_FILES.pulip;
    if (!_notiAudio || _notiAudioKey !== key) {
      _notiAudio = new Audio(src);
      _notiAudio.preload = 'auto';
      _notiAudioKey = key;
    }
    _notiAudio.currentTime = 0;
    const p = _notiAudio.play();
    if (p && p.catch) p.catch(() => {});
  } catch {}
};

const slideIn = keyframes`
  from { transform: translateX(120%); opacity: 0; }
  to   { transform: translateX(0); opacity: 1; }
`;

const slideOut = keyframes`
  from { transform: translateX(0); opacity: 1; }
  to   { transform: translateX(120%); opacity: 0; }
`;

const Container = styled.div`
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 99999;
  display: flex;
  flex-direction: column;
  gap: 10px;
  pointer-events: none;
  @media (max-width: 768px) {
    right: 12px;
    bottom: 80px;
    left: 12px;
  }
`;

const Card = styled.button`
  pointer-events: auto;
  width: 340px;
  max-width: calc(100vw - 24px);
  background: #ffffff;
  border: 1px solid #E2E8F0;
  border-radius: 14px;
  box-shadow: 0 8px 28px rgba(15, 23, 42, 0.18), 0 2px 8px rgba(15, 23, 42, 0.08);
  padding: 12px 36px 12px 14px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  position: relative;
  animation: ${slideIn} 220ms cubic-bezier(0.22, 1, 0.36, 1);
  ${({ $closing }) => $closing && css`
    animation: ${slideOut} 220ms cubic-bezier(0.55, 0, 0.68, 0.19) forwards;
  `}
  &:hover {
    border-color: #CBD5E1;
    box-shadow: 0 10px 32px rgba(15, 23, 42, 0.22);
  }
  &:active {
    transform: translateY(1px);
  }
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Avatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #E8F2EC url('/plhouse-logo.png') center/70% no-repeat;
  flex-shrink: 0;
  border: 1px solid #D1E7DA;
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-size: 13.5px;
  font-weight: 700;
  color: #0F172A;
  line-height: 1.3;
  margin-bottom: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Text = styled.div`
  font-size: 12.5px;
  color: #475569;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CloseBtn = styled.span`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #94A3B8;
  background: transparent;
  &:hover {
    background: #F1F5F9;
    color: #0F172A;
  }
`;

function ToastItem({ toast, onClose }) {
  const [closing, setClosing] = useState(false);

  const close = useCallback(() => {
    setClosing(true);
    setTimeout(() => onClose(toast.id), 220);
  }, [toast.id, onClose]);

  useEffect(() => {
    const t = setTimeout(close, TOAST_DURATION_MS);
    return () => clearTimeout(t);
  }, [close]);

  // 알림 클릭 시 라우팅 없이 토스트만 닫음 (이동 동작 보류)
  const handleClick = () => {
    close();
  };

  return (
    <Card $closing={closing} onClick={handleClick} type="button">
      <Avatar />
      <Body>
        <Title>{toast.title || '풀잎하우스'}</Title>
        <Text>{toast.body || ''}</Text>
      </Body>
      <CloseBtn
        onClick={(e) => { e.stopPropagation(); close(); }}
        role="button"
        aria-label="닫기"
      >×</CloseBtn>
    </Card>
  );
}

export default function InAppToastHost() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail || {};
      const item = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        notiId: detail.notiId || detail.data?.notiId || '',
        title: detail.title || detail.notification?.title,
        body: detail.body || detail.notification?.body,
        type: detail.type || detail.data?.type,
        data: detail.data || {},
      };
      setToasts((prev) => {
        // 같은 알림 id 중복(같은 소스 두 번) 방지
        if (item.notiId && prev.some((t) => t.notiId === item.notiId)) return prev;
        const next = [...prev, item];
        return next.length > MAX_STACK ? next.slice(next.length - MAX_STACK) : next;
      });
      // 실제 알림(타입/알림ID 있는 것)만 풀잎 소리 — 안내성 토스트는 무음
      if (item.notiId || item.type) playNotiSound();
    };
    window.addEventListener(TOAST_EVENT, handler);
    return () => window.removeEventListener(TOAST_EVENT, handler);
  }, []);

  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  if (!toasts.length) return null;

  return (
    <Container>
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onClose={remove} />
      ))}
    </Container>
  );
}
